import { registerPlugin } from '@capacitor/core';

/**
 * Puente con `RoisPhotoPermissionsPlugin.java`: los dos permisos que necesita
 * una foto para guardar dónde se hizo (spec 2026-09-23 fotos geolocalizadas,
 * § 14 y § 15). Los exige `usePhotoPermissionsGate`.
 *
 *  - **Ubicación precisa**: para que la cámara de ROIS ponga el GPS.
 *  - **Ubicación de las fotos** (`ACCESS_MEDIA_LOCATION`): para que las fotos
 *    de la galería no lleguen con el GPS a ceros.
 *
 * 🔴 Solo existe en las APK que ya lo traen. En una APK vieja (el código web
 * llega por OTA antes que la APK nueva) las llamadas se rechazan, y quien las
 * use tiene que tratarlo como «no se puede pedir», nunca como «denegado».
 */

/**
 * Estado de un permiso tal como lo da Capacitor.
 *  - `prompt`: nunca se ha preguntado.
 *  - `prompt-with-rationale`: negado una vez; el diálogo todavía sale.
 *  - `denied`: Android ya no enseña el diálogo; solo queda Ajustes.
 */
export type PermissionStep =
  | 'granted'
  | 'denied'
  | 'prompt'
  | 'prompt-with-rationale';

export interface LocationPermissionStatus {
  /** `granted` solo con la ubicación PRECISA. */
  state: PermissionStep;
  /**
   * Tiene la aproximada y no la precisa. Con ella el error es de kilómetros y
   * la foto se queda sin GPS: el modal pide «Cambiar a ubicación precisa».
   */
  approximate: boolean;
}

export interface PhotosPermissionStatus {
  /**
   * `false` en Android 9 o anterior: ahí el permiso no existe y las fotos ya
   * llegan con su ubicación. Entonces `state` es `'granted'`.
   */
  required: boolean;
  state: PermissionStep;
  /**
   * Eligió «Permitir acceso limitado», que NO da la ubicación de las fotos.
   * Sirve para explicárselo en ese momento, en vez de nombrar de antemano la
   * opción que no sirve.
   */
  limited: boolean;
}

export interface PhotoPermissionsStatus {
  location: LocationPermissionStatus;
  photos: PhotosPermissionStatus;
}

/**
 * Permiso de la cámara, solo consultado (la cámara la pide el WebView al
 * abrirla). `canAsk`: negado, pero Android todavía enseñará el diálogo; si es
 * `false` sin `granted`, solo queda Ajustes.
 */
export interface CameraAccess {
  granted: boolean;
  canAsk: boolean;
}

export interface RoisPhotoPermissionsPlugin {
  /** Estado actual de los dos, sin preguntar nada. */
  status(): Promise<PhotoPermissionsStatus>;
  /** Diálogo de ubicación. Solo desde un botón, nunca al volver a la app. */
  requestLocation(): Promise<PhotoPermissionsStatus>;
  /** Diálogo de «fotos y vídeos». Solo desde un botón. */
  requestPhotos(): Promise<PhotoPermissionsStatus>;
  /** Permiso de la cámara, sin preguntar nada. */
  cameraAccess(): Promise<CameraAccess>;
}

export const RoisPhotoPermissions =
  registerPlugin<RoisPhotoPermissionsPlugin>('RoisPhotoPermissions');

export default RoisPhotoPermissions;
